import { useState } from 'react';
import Button from '../../components/common/Button';
import { useToast } from '../../context/ToastContext';
import './FraudConfig.css';

const COUNTRIES = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
];

const FraudConfig = () => {
  const { showToast } = useToast();

  const [config, setConfig] = useState({
    voipBlocking: true,
    allowedCountries: ['US', 'CA', 'GB'],
    maxAttempts15Min: 5,
    maxAttemptsHour: 10,
    botThreshold: 75,
    deviceFingerprint: true,
    blockTor: true,
    blockVPN: true,
    blockProxies: false,
    autoBlockFailedAttempts: true,
    autoBlockVelocity: true,
    autoBlockHighRisk: false,
    blockScoreThreshold: 80,
    flagScoreThreshold: 50,
    allowScoreThreshold: 50,
  });

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleCountryChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, option => option.value);
    updateConfig('allowedCountries', selected);
  };

  const handleSave = () => {
    showToast('Configuration saved successfully', 'success');
  };

  const handleReset = () => {
    showToast('Configuration reset to defaults', 'info');
  };

  return (
    <div className="fraud-config-page">
      <div className="section">
        <div className="section-header">
          <h2 className="section-title">Fraud Detection Configuration</h2>
          <span className="badge badge-info">Tenant: Cachee</span>
        </div>

        <div className="alert alert-info">
          Configure fraud detection rules specific to this tenant. Changes apply immediately.
        </div>

        {/* VOIP Blocking */}
        <div className="config-card">
          <div className="config-card-header">
            <div>
              <h4 className="config-title">VOIP Number Blocking</h4>
              <p className="config-desc">Block authentication attempts from VOIP/virtual phone numbers</p>
            </div>
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={config.voipBlocking}
                onChange={(e) => updateConfig('voipBlocking', e.target.checked)}
              />
              <span className={`toggle-status ${config.voipBlocking ? 'enabled' : 'disabled'}`}>
                {config.voipBlocking ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>
        </div>

        {/* Country Restrictions */}
        <div className="config-card">
          <h4 className="config-title">Allowed Countries</h4>
          <p className="config-desc">Select countries where authentication is permitted. All others will be blocked.</p>

          <select
            multiple
            className="country-select"
            size={6}
            value={config.allowedCountries}
            onChange={handleCountryChange}
          >
            {COUNTRIES.map(country => (
              <option key={country.code} value={country.code}>
                {country.flag} {country.name}
              </option>
            ))}
          </select>
          <p className="hint">Hold Ctrl/Cmd to select multiple</p>

          <div className="current-allowed">
            <strong>Currently Allowed:</strong>
            <div className="country-badges">
              {config.allowedCountries.map(code => (
                <span key={code} className="badge badge-success">{code}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Velocity Limits */}
        <div className="config-card">
          <h4 className="config-title">Velocity Limits</h4>
          <p className="config-desc">Maximum authentication attempts per identifier within a time window</p>

          <div className="form-group">
            <label className="form-label">Max Attempts per 15 minutes</label>
            <input
              type="number"
              className="form-input"
              value={config.maxAttempts15Min}
              onChange={(e) => updateConfig('maxAttempts15Min', parseInt(e.target.value))}
              min={1}
              max={20}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Max Attempts per Hour</label>
            <input
              type="number"
              className="form-input"
              value={config.maxAttemptsHour}
              onChange={(e) => updateConfig('maxAttemptsHour', parseInt(e.target.value))}
              min={1}
              max={50}
            />
          </div>
        </div>

        {/* Bot Detection */}
        <div className="config-card">
          <h4 className="config-title">Bot Detection Threshold</h4>
          <p className="config-desc">Block requests with bot score above this threshold (0-100)</p>

          <div className="form-group">
            <label className="form-label">
              Bot Score Threshold: <span className="threshold-value">{config.botThreshold}</span>
            </label>
            <input
              type="range"
              min={0}
              max={100}
              value={config.botThreshold}
              onChange={(e) => updateConfig('botThreshold', parseInt(e.target.value))}
              className="range-slider"
            />
            <div className="range-labels">
              <span>Lenient (0)</span>
              <span>Moderate (50)</span>
              <span>Strict (100)</span>
            </div>
          </div>
        </div>

        {/* Device Fingerprinting */}
        <div className="config-card">
          <div className="config-card-header">
            <div>
              <h4 className="config-title">Device Fingerprint Required</h4>
              <p className="config-desc">Require client to send device fingerprint with each request</p>
            </div>
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={config.deviceFingerprint}
                onChange={(e) => updateConfig('deviceFingerprint', e.target.checked)}
              />
              <span className={`toggle-status ${config.deviceFingerprint ? 'enabled' : 'disabled'}`}>
                {config.deviceFingerprint ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>
        </div>

        {/* Anonymous Traffic Blocking */}
        <div className="config-card">
          <h4 className="config-title">Anonymous Traffic Blocking</h4>

          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.blockTor}
                onChange={(e) => updateConfig('blockTor', e.target.checked)}
              />
              <span>Block Tor</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.blockVPN}
                onChange={(e) => updateConfig('blockVPN', e.target.checked)}
              />
              <span>Block VPN</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.blockProxies}
                onChange={(e) => updateConfig('blockProxies', e.target.checked)}
              />
              <span>Block Proxies</span>
            </label>
          </div>
        </div>

        {/* Auto-Block Rules */}
        <div className="config-card">
          <h4 className="config-title">Auto-Block Rules</h4>
          <p className="config-desc">Automatically block IPs/devices that trigger these conditions</p>

          <div className="checkbox-list">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.autoBlockFailedAttempts}
                onChange={(e) => updateConfig('autoBlockFailedAttempts', e.target.checked)}
              />
              <span>Block after 3 failed verification attempts</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.autoBlockVelocity}
                onChange={(e) => updateConfig('autoBlockVelocity', e.target.checked)}
              />
              <span>Block after 5 velocity limit violations</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.autoBlockHighRisk}
                onChange={(e) => updateConfig('autoBlockHighRisk', e.target.checked)}
              />
              <span>Block on first fraud signal (high-risk countries)</span>
            </label>
          </div>
        </div>

        {/* Risk Score Thresholds */}
        <div className="config-card">
          <h4 className="config-title">Risk Score Actions</h4>

          <div className="score-grid">
            <div className="form-group">
              <label className="form-label">Block if score ≥</label>
              <input
                type="number"
                className="form-input"
                value={config.blockScoreThreshold}
                onChange={(e) => updateConfig('blockScoreThreshold', parseInt(e.target.value))}
                min={0}
                max={100}
              />
              <p className="hint">Auto-block high risk</p>
            </div>
            <div className="form-group">
              <label className="form-label">Flag if score ≥</label>
              <input
                type="number"
                className="form-input"
                value={config.flagScoreThreshold}
                onChange={(e) => updateConfig('flagScoreThreshold', parseInt(e.target.value))}
                min={0}
                max={100}
              />
              <p className="hint">Manual review queue</p>
            </div>
            <div className="form-group">
              <label className="form-label">Allow if score &lt;</label>
              <input
                type="number"
                className="form-input"
                value={config.allowScoreThreshold}
                onChange={(e) => updateConfig('allowScoreThreshold', parseInt(e.target.value))}
                min={0}
                max={100}
              />
              <p className="hint">Auto-allow low risk</p>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <Button variant="secondary" onClick={handleReset}>Reset to Platform Defaults</Button>
          <Button variant="primary" onClick={handleSave}>Save Configuration</Button>
        </div>
      </div>
    </div>
  );
};

export default FraudConfig;
